/**
 * LJ V3 design tokens — Vogue.com-inspired editorial system.
 *
 * Real Vogue.com uses a sans-serif (Vogue Sans / Helvetica Now-style)
 * for ALL editorial text — section titles, story headlines, deks, body,
 * UI. The only Didone serif is the "VOGUE" masthead logo itself.
 *
 * So this token system follows the same rule: Lexend for everything,
 * Playfair Display reserved exclusively for the masthead wordmark.
 *
 * Hierarchy is achieved through font weight, size, case, and tracking —
 * never colour.
 */

export const LJ_COLORS = {
  // text
  textPrimary: "#000000",
  textBody: "#1a1a1a",
  textSecondary: "#6f6f6f",
  textMuted: "#8e8e8e",
  textOnDark: "#ffffff",
  textOnDarkMuted: "rgba(255,255,255,0.6)",

  // surfaces
  bgPage: "#ffffff",
  bgSection: "#ffffff",
  bgSubtle: "#f5f5f5",
  bgFooter: "#000000",
  bgTicker: "#ffffff",

  // borders / rules — Vogue is built on hairlines
  dividerHair: "#d6d6d6",
  dividerSoft: "#ececec",
  dividerStrong: "#000000",
  dividerOnDark: "rgba(255,255,255,0.2)",

  // interactive
  ctaBorder: "#000000",
  ctaHover: "#000000",

  // video / overlay accents
  durationChipBg: "rgba(0,0,0,0.75)",
  playButtonBg: "rgba(255,255,255,0.60)",
} as const;

export const LJ_FONT = {
  // Wordmark ONLY — the "LIFESTYLE JOURNAL" masthead. Mirrors Vogue's
  // Didone "VOGUE" logo treatment. Never use for editorial headlines.
  wordmark: `"Playfair Display", "Noto Serif TC", Georgia, serif`,
  // Everything else — section titles, story headlines, deks, body, UI.
  // Vogue is fundamentally a sans-serif site in its 2020s redesign.
  sans: `Lexend, "Helvetica Neue", Helvetica, "Noto Sans TC", system-ui, -apple-system, sans-serif`,
} as const;

/**
 * Reusable type recipes — spread into a `style={{}}` to apply.
 * Lexend handles all editorial roles. Weight + tracking + case do the work.
 */
export const LJ_TYPE = {
  // Top news ticker, footer column heads, "Advertisement" labels
  micro: {
    fontFamily: LJ_FONT.sans,
    fontSize: 11,
    lineHeight: "14px",
    fontWeight: 600,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
  },
  // Card category eyebrow ("FASHION", "WATCH & JEWELRY")
  eyebrow: {
    fontFamily: LJ_FONT.sans,
    fontSize: 10,
    lineHeight: "16px",
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
  },
  // Top nav links
  navLink: {
    fontFamily: LJ_FONT.sans,
    fontSize: 11,
    lineHeight: "14px",
    fontWeight: 600,
    letterSpacing: "0.2em",
    textTransform: "uppercase" as const,
  },
  // Byline ("By 編輯室") under every card
  byline: {
    fontFamily: LJ_FONT.sans,
    fontSize: 12,
    lineHeight: "16px",
    fontWeight: 500,
    letterSpacing: "0.14em",
    textTransform: "uppercase" as const,
  },
  // Masthead "LIFESTYLE JOURNAL" — the ONLY serif use
  wordmark: {
    fontFamily: LJ_FONT.wordmark,
    fontWeight: 400,
    letterSpacing: "0.04em",
  },
  // Section header ("Latest", "Most Read", "Editor's Picks") — Vogue's
  // section titles are sans-serif and surprisingly small / understated.
  sectionTitle: {
    fontFamily: LJ_FONT.sans,
    fontSize: 14,
    lineHeight: "18px",
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
  },
  // Hero / lead-story headline — oversized sans, light weight
  heroHed: {
    fontFamily: LJ_FONT.sans,
    fontWeight: 500,
    letterSpacing: "-0.01em",
  },
  // Article-card title (standard grid)
  cardHed: {
    fontFamily: LJ_FONT.sans,
    fontSize: 18,
    lineHeight: "23px",
    fontWeight: 500,
    letterSpacing: "-0.005em",
  },
  // Smaller card title (4-up support tiles, river thumbs)
  cardHedSm: {
    fontFamily: LJ_FONT.sans,
    fontSize: 16,
    lineHeight: "21px",
    fontWeight: 500,
    letterSpacing: "-0.005em",
  },
  // Editorial dek (intro paragraph under a hero headline)
  dek: {
    fontFamily: LJ_FONT.sans,
    fontSize: 16,
    lineHeight: "24px",
    fontWeight: 400,
  },
  // Body copy
  body: {
    fontFamily: LJ_FONT.sans,
    fontSize: 15,
    lineHeight: "24px",
    fontWeight: 400,
  },
} as const;
